/**
 * Phase 20 Plan 20-02 — AlertFilterBar
 *
 * 4 filter controls: status / severity / notificationType / from / to date pickers.
 * onChange fires with an updated AlertListQuery on every field change.
 *
 * Phase 21 Plan 21-02 — D-198 chip preset for "Daily Report" quick-filter.
 */

import type { JSX } from 'react';

import { useNotificationTypesQuery } from '@/hooks/useNotificationsQueries';
import { cn } from '@/lib/utils/cn';
import type { AlertListQuery, AlertSeverity, AlertStatus, NotificationTypeValue } from '@/types/notifications';

interface AlertFilterBarProps {
  readonly filters: AlertListQuery;
  readonly onChange: (filters: AlertListQuery) => void;
}

export function AlertFilterBar({ filters, onChange }: AlertFilterBarProps): JSX.Element {
  const typesQuery = useNotificationTypesQuery();

  const handleStatus = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    onChange({ ...filters, status: e.target.value as AlertStatus | '' });
  };

  const handleSeverity = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    onChange({ ...filters, severity: e.target.value as AlertSeverity | '' });
  };

  const handleType = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    onChange({ ...filters, notificationType: e.target.value as NotificationTypeValue | '' });
  };

  const handleFrom = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange({ ...filters, from: e.target.value || undefined });
  };

  const handleTo = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange({ ...filters, to: e.target.value || undefined });
  };

  const handleClear = (): void => {
    onChange({ status: '', severity: '', notificationType: '', from: undefined, to: undefined });
  };

  return (
    <div className="flex flex-wrap items-end gap-3 pb-4" data-testid="alert-filter-bar">
      {/* Status */}
      <div className="flex flex-col gap-1">
        <label htmlFor="filter-status" className="text-xs text-muted-foreground">
          Status
        </label>
        <select
          id="filter-status"
          data-testid="filter-status"
          value={filters.status ?? ''}
          onChange={handleStatus}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All</option>
          <option value="New">New</option>
          <option value="Acknowledged">Acknowledged</option>
          <option value="Dismissed">Dismissed</option>
        </select>
      </div>

      {/* Severity */}
      <div className="flex flex-col gap-1">
        <label htmlFor="filter-severity" className="text-xs text-muted-foreground">
          Severity
        </label>
        <select
          id="filter-severity"
          data-testid="filter-severity"
          value={filters.severity ?? ''}
          onChange={handleSeverity}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All</option>
          <option value="Info">Info</option>
          <option value="Warning">Warning</option>
          <option value="Critical">Critical</option>
        </select>
      </div>

      {/* Notification Type */}
      <div className="flex flex-col gap-1">
        <label htmlFor="filter-notification-type" className="text-xs text-muted-foreground">
          Type
        </label>
        <select
          id="filter-notification-type"
          data-testid="filter-notification-type"
          value={filters.notificationType ?? ''}
          onChange={handleType}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All</option>
          {(typesQuery.data ?? []).map((t) => (
            <option key={t.value} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {/* From date */}
      <div className="flex flex-col gap-1">
        <label htmlFor="filter-from" className="text-xs text-muted-foreground">
          From
        </label>
        <input
          id="filter-from"
          type="date"
          data-testid="filter-from"
          value={filters.from ?? ''}
          onChange={handleFrom}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        />
      </div>

      {/* To date */}
      <div className="flex flex-col gap-1">
        <label htmlFor="filter-to" className="text-xs text-muted-foreground">
          To
        </label>
        <input
          id="filter-to"
          type="date"
          data-testid="filter-to"
          value={filters.to ?? ''}
          onChange={handleTo}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        />
      </div>

      {/* Clear */}
      <button
        type="button"
        data-testid="filter-clear"
        onClick={handleClear}
        className="h-8 rounded-md border border-input bg-background px-3 text-sm hover:bg-accent"
      >
        Clear
      </button>

      {/* Daily Report chip preset — D-198 */}
      <button
        type="button"
        data-testid="filter-chip-daily-report"
        aria-pressed={filters.notificationType === 'DailyReport'}
        className={cn(
          'h-8 rounded-full border px-3 text-xs',
          filters.notificationType === 'DailyReport'
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input bg-background',
        )}
        onClick={() => {
          onChange({
            ...filters,
            notificationType: filters.notificationType === 'DailyReport' ? '' : 'DailyReport',
          });
        }}
      >
        Daily Report
      </button>
    </div>
  );
}
